import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound } from 'lucide-react';

import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Spinner } from '../../../components/ui/spinner';
import PageWrap from '../../../components/auth/page-wrap';
import { getErrorStatus } from '../../../core/api/response-handler';
import { confirmaCod, deschideSesiune, type ScoalaConfirmata } from '../api/cod-scoala.api';
import { textEroare, textLocal, validateazaCod } from '../utils/cod-scoala';

/**
 * Intrarea in cont cu codul scolii (SP-05b, b24).
 *
 * 🔴 Doi pasi, si al doilea nu e o formalitate: codul spune CINE ESTI, iar confirmarea
 * («Ești colaborator la <scoala>?») e singurul moment in care omul poate observa ca a nimerit alta
 * scoala. Fara ea, un cod tastat gresit care se intampla sa fie valid l-ar duce direct in
 * activitatea altcuiva, si ar afla abia din continut.
 *
 * 🔴 O cerere per apasare de buton. ZERO verificare pe tastare, zero reincercare automata.
 * Motivul e masurat, nu stilistic: fiecare cod gresit produce azi un 404 care scrie un rand in
 * `discord_error_logs` si o alerta Discord (V1 pe DEV, 2026-09-07: 24 de probe, 57035 → 57059).
 * Comportamentul e mostenit din `CollaboratorLinkService` si il repara B1 separat — dar un camp cu
 * validare pe tastare ar fi produs un incident PE LITERA. Cand reparatia ajunge in PROD, regula
 * ramane: nu trimitem cereri pe care omul nu le-a cerut.
 *
 * ⚠️ Mesajul de esec e UNIFORM pentru orice raspuns in afara de 429, si nu e o scapare de
 * design: serverul intoarce corpuri identice pe octeti pentru „cod inexistent", „scoala fara
 * profesor" si „scoala din alta editie". Un text care ar distinge intre ele ar reconstrui oracolul
 * de enumerare inchis in backend, exact in locul pe care il vede atacatorul. Textele stau in
 * `utils/cod-scoala.ts`, sub test, ca sa nu se rescrie „mai prietenos" fara sa pice ceva.
 *
 * 🔴 Grupul 5 (S1, PR #174): pagina traia in afara sistemului de card folosit de restul
 * fluxului A (fara `PageWrap`/`Card`, camp cu fundal transparent direct pe fundalul cu
 * tipar) — acum e aliniata cu celelalte. Alinierea textului in camp: ALEASA centrata, ca
 * pe majoritatea campurilor din flux (6 din 7 pagini publice folosesc `.form-input-styled`,
 * care centreaza) — nu inventez o a doua regula.
 */

export default function CodScoalaPage() {
    const navigate = useNavigate();

    const [cod, setCod] = useState('');
    const [scoala, setScoala] = useState<ScoalaConfirmata | null>(null);
    /** Codul EXACT trimis la pasul 1 — pasul 2 il refoloseste, nu recitieste campul. */
    const [codTrimis, setCodTrimis] = useState('');
    const [eroare, setEroare] = useState<string | null>(null);
    const [seLucreaza, setSeLucreaza] = useState(false);
    const campRef = useRef<HTMLInputElement>(null);

    const verifica = async () => {
        if (seLucreaza) return;
        const validat = validateazaCod(cod);
        if (!validat.valid) {
            // Nu pleaca nicio cerere: stim dinainte ca serverul ar raspunde cu un refuz, si refuzul
            // ala are un cost in afara ecranului.
            setEroare(textLocal(validat.motiv));
            campRef.current?.focus();
            return;
        }
        setEroare(null);
        // 🔴 Normalizarea se vede INAINTE de a pleca cererea (cerinta S1): daca omul scrie
        //    `se 1234` si noi trimitem `SE1234`, campul trebuie sa arate ce am inteles. Altfel un
        //    cod respins il lasa sa se intrebe daca a gresit el sau noi — si il face sa mai incerce
        //    o data acelasi lucru, adica inca un 404 si inca un rand in log.
        setCod(validat.cod);
        setSeLucreaza(true);
        try {
            const raspuns = await confirmaCod(validat.cod);
            setScoala(raspuns);
            setCodTrimis(validat.cod);
        } catch (e) {
            setScoala(null);
            setEroare(textEroare(getErrorStatus(e)));
            campRef.current?.focus();
        } finally {
            setSeLucreaza(false);
        }
    };

    const intra = async () => {
        if (seLucreaza || !codTrimis) return;
        setEroare(null);
        setSeLucreaza(true);
        try {
            const token = await deschideSesiune(codTrimis);
            // `token` e garantat nevid de `deschideSesiune` — tipul generat il da optional, si un
            // `navigate` naiv ar duce la `/activitate/undefined`.
            navigate(`/activitate/${encodeURIComponent(token)}`);
        } catch (e) {
            setEroare(textEroare(getErrorStatus(e)));
        } finally {
            setSeLucreaza(false);
        }
    };

    return (
        <PageWrap>
            <Card className="mx-auto w-full max-w-md">
                <CardContent className="flex flex-col gap-4 pt-6">
                    <div className="flex items-start gap-3">
                        <KeyRound className="size-9 shrink-0 text-magenta" aria-hidden />
                        <div className="min-w-0">
                            <h1 className="font-heading text-fluid-2xl font-semibold text-dark m-0">
                                Intră cu codul școlii
                            </h1>
                            <p className="mt-2 text-fluid-base text-dark m-0">
                                Codul îl primești de la profesorul coordonator. Nu ai nevoie de cont și de
                                parolă.
                            </p>
                        </div>
                    </div>

                    {/* Pasul 1: campul. Ramane pe ecran si dupa confirmare, ca omul sa poata corecta un
                        cod gresit fara sa se intoarca. */}
                    <div>
                        <Label htmlFor="cod-scoala">Codul școlii</Label>
                        <Input
                            id="cod-scoala"
                            ref={campRef}
                            name="cod-scoala"
                            autoComplete="off"
                            autoCapitalize="characters"
                            spellCheck={false}
                            className="form-input-styled mt-1 min-h-11"
                            aria-required="true"
                            aria-invalid={!!eroare}
                            value={cod}
                            onChange={(e) => {
                                setCod(e.target.value);
                                // Confirmarea veche nu mai corespunde codului din camp: se retrage, ca
                                // sa nu existe un moment in care butonul «Da, intru» ar trimite alt cod
                                // decat scrie pe ecran.
                                if (scoala) setScoala(null);
                                if (eroare) setEroare(null);
                            }}
                        />
                    </div>

                    {eroare && (
                        <div
                            data-eroare-cod
                            role="alert"
                            className="rounded-lg border border-error bg-error/10 p-3 text-small text-error"
                        >
                            {eroare}
                        </div>
                    )}

                    {/* Pasul 2: confirmarea. Intrebare, nu anunt — raspunsul „nu" e la fel de valid. */}
                    {scoala && (
                        <div data-cod-confirmare className="rounded-lg border border-magenta p-3">
                            <p className="m-0 text-small text-dark">
                                Ești colaborator la <strong>{scoala.numeScoala}</strong>?
                            </p>
                            {scoala.numeEditie && (
                                <p className="m-0 mt-1 text-smaller text-gray-600">{scoala.numeEditie}</p>
                            )}
                            <Button
                                type="button"
                                onClick={intra}
                                aria-disabled={seLucreaza}
                                aria-busy={seLucreaza}
                                className="mt-3 flex min-h-11 w-full items-center justify-center gap-2"
                            >
                                {seLucreaza && <Spinner aria-hidden className="size-4" />}
                                {seLucreaza ? 'Se deschide…' : 'Da, continuă'}
                            </Button>
                            {/* 🔴 Calea de NU nu e decor: o confirmare cu un singur buton nu e o
                                confirmare, e un anunt (S1, directia (d) §2). Se intoarce la camp cu
                                codul PASTRAT, ca omul sa corecteze o litera, nu sa retasteze tot. */}
                            <Button
                                type="button"
                                onClick={() => setScoala(null)}
                                className="mt-2 min-h-11 w-full border border-input bg-white font-semibold text-dark hover:bg-light"
                            >
                                Nu, altă școală
                            </Button>
                        </div>
                    )}

                    {/* Un singur buton plin per grup: cat timp exista confirmarea, ea e actiunea
                        principala, iar «Verifică codul» trece pe conturat.
                        `min-h-[76px]`: rezerva inaltimea butonului plus spatiul dintre el si camp,
                        ca aparitia cardului de confirmare de mai sus sa nu mai mute vizual actiunea
                        principala cu 133px (S0-1, runda 3) -- masurat pe fluxul real: 16px, nu 133. */}
                    <div className="min-h-[76px]">
                        {!scoala && (
                            <Button
                                type="button"
                                onClick={verifica}
                                aria-disabled={seLucreaza}
                                aria-busy={seLucreaza}
                                className="flex min-h-11 w-full items-center justify-center gap-2"
                            >
                                {seLucreaza && <Spinner aria-hidden className="size-4" />}
                                {seLucreaza ? 'Se verifică…' : 'Verifică codul'}
                            </Button>
                        )}
                    </div>
                </CardContent>
            </Card>
        </PageWrap>
    );
}
